import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { useHospiceConvertToPending } from "../../../store/useHospiceConvertToPending";
import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { IPatientDme } from "../../../../../../resources-vue/vue-interfaces/i-get-dme";
import { DME } from "../../../../../../resources-vue/vue-models/dme";
import { DMEService } from "../../../../../../services/dme.service";
import { ToastrService } from "../../../../../../services/toastr.service";
import { EnumsService } from "../../../../../../services/enums.service";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import TableEmpty from "../../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import PatientDMEVendor from "./PatientDMEVendor.vue";
import DMETableRow from "./DMETableRow.vue";

export default defineComponent({
    components: {
        FormSection,
        PatientDMEVendor,
        TypeaheadInput,
        TableEmpty,
        DMETableRow
    },
    props: {
        patientVendors: { type: Array as PropType<string[]>, default: [] },
        providerId: {
            type: String,
            default: ""
        },
        orderId: {
            type: String,
            default: null
        },
        isReferral: {
            type: Boolean,
            default: false
        },
        patientStatus: {
            type: Number,
            default: null
        },
        dmes: { type: Array as PropType<IPatientDme[]>, default: [] }
    },
    data() {
        return {
            columns: 6,
            patientStatusEnums: [] as IEnumResponse[],
            dmeList: [] as DME[],
            selectionDme: null as ITypeaheadOptions,
            savetoAdd: null as IPatientDme,
            showDmeInputRow: null as boolean,
            _dmeService: null,
            _toastrService: null,
            _enumsService: null
        };
    },
    computed: {
        ...mapState(useHospiceConvertToPending, ["accountId", "application"]),
        isEditAllowed() {
            let pendingStatus = this.patientStatusEnums.find((status) => status.name.toLowerCase().includes("pending"));
            return !!this.orderId || this.isReferral || (pendingStatus && this.patientStatus == pendingStatus.value);
        }
    },
    watch: {
        selectionDme(newValue) {
            let dmeSelected: DME;
            if (newValue) {
                dmeSelected = this.dmeList.find((dme) => dme.Id == newValue.value);
                if (dmeSelected) {
                    this.savetoAdd = {
                        id: null,
                        agencyDMEId: dmeSelected.Id,
                        name: dmeSelected.Name,
                        isInfusionPump: dmeSelected.IsInfusionPump,
                        startDate: "",
                        endDate: "",
                        isRelated: null,
                        notRelatedComments: null
                    };
                    this.showDmeInputRow = true;
                }
            } else {
                this.showDmeInputRow = false;
            }
        }
    },
    emits: ["update:patientVendors", "update:dmes"],
    created() {
        this._dmeService = Container.instance.get(DMEService);
        this._toastrService = Container.instance.get(ToastrService);
        this._enumsService = Container.instance.get(EnumsService);
    },
    async mounted() {
        let patientStatusEnums = await this._enumsService.getPatientStatus();
        this.patientStatusEnums = patientStatusEnums.getJson();
        if (!this.patientVendors?.length) {
            this.$emit("update:patientVendors", [null]);
        }
    },
    methods: {
        addNewVendor() {
            const patientVendors = this.patientVendors.concat(null);
            this.$emit("update:patientVendors", patientVendors);
        },
        async getDMEList(filter: string) {
            try {
                if (filter.length > 0) {
                    let params = {
                        term: filter,
                        providerIds: [this.providerId],
                        application: this.application,
                        accountId: this.accountId
                    };
                    this.dmeList = await this._dmeService.getDMEByTerm(params);
                    return this.displayPatientDME(this.dmeList);
                } else {
                    return [];
                }
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: "Error",
                    message: "There was a problem fetching DME List. Please try again."
                });
                throw e;
            }
        },
        displayPatientDME(getDME: DME[]): ITypeaheadOptions[] {
            return getDME.map((value: DME, index, number) => ({
                value: value.Id,
                name: value.Name
            }));
        },
        selectionDmeChanged(data: ITypeaheadOptions) {
            this.selectionDme = data;
        },
        saveNewDme(newDme: IPatientDme, oldDme: IPatientDme) {
            let existingDme = this.dmes.find(
                (dme) =>
                    dme.agencyDMEId == newDme.agencyDMEId &&
                    dme.startDate == newDme.startDate &&
                    dme.endDate == newDme.endDate
            );
            if (existingDme) {
                this._toastrService.info({
                    title: `${existingDme.name} already exists`,
                    message: `${existingDme.name} is already assigned to patient between ${existingDme.startDate} and ${existingDme.endDate}.`
                });
            } else {
                this.dmes.push(newDme);
            }
            this.cancelNewDme();
        },
        cancelNewDme() {
            this.savetoAdd = {
                id: null,
                isRelated: null,
                notRelatedComments: null,
                agencyDMEId: null,
                name: null,
                isInfusionPump: null,
                startDate: null,
                endDate: null
            };
            this.selectionDme = null;
            this.showDmeInputRow = false;
        },
        updateDme(newDme: IPatientDme, oldDme: IPatientDme) {
            let dmeIndex = this.dmes.findIndex(
                (dme) =>
                    dme.agencyDMEId == oldDme.agencyDMEId &&
                    dme.startDate == oldDme.startDate &&
                    dme.endDate == oldDme.endDate
            );
            if (dmeIndex > -1) {
                this.dmes[dmeIndex] = newDme;
            }
        },
        deleteDmeFromList(deleteDme: IPatientDme) {
            let dmes = this.dmes.filter(
                (dme) =>
                    !(
                        dme.agencyDMEId == deleteDme.agencyDMEId &&
                        dme.startDate == deleteDme.startDate &&
                        dme.endDate == deleteDme.endDate
                    )
            );
            this.$emit("update:dmes", dmes);
        }
    }
});
